import { getStoreImages } from '@api/store';
import StoreReview from '@components/reviews/store-review';
import { TStore, TStoreTiming } from '@components/types';
import appConfig from '@core/appconfig';
import { getLocation } from '@core/geolocation';
import { formatStoreHours, formatStoreTime, getAddress, hasStoreHours, isTimingOpenNow, getSafeUrl, sanityIoImageLoader, suggestColor } from '@core/utils';
import { useInView, useMotionValueEvent, useScroll } from 'framer-motion';
import { getDistance } from 'geolib';
import { Modal } from 'antd';
import { BadgeCheck, ChevronRight, Clock, MapPin, Navigation, Phone, RotateCcw, Share2, Star, Store, Truck } from 'lucide-react';
import { PLACEHOLDERS, SafeImage } from '@components/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { RWebShare } from 'react-web-share';

interface StoreTopCardProps {
  store: TStore;
  storeTimings: TStoreTiming[];
  isAvailable?: boolean;
}

export function StoreTopCard({
  store,
  storeTimings,
  isAvailable,
}: StoreTopCardProps) {
  const [storeImages, setStoreImages] = useState([]);
  const [localDistance, setLocalDistance] = useState<number>();
  const [isVisible, setIsVisible] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  const ref = useRef();
  const isInView = useInView(ref);
  const { scrollY } = useScroll();

  async function fetchStoreImages() {
    const data = await getStoreImages(store?.id);
    setStoreImages(data);
  }

  async function fetchLocation() {
    const location = await getLocation();
    const distance = getDistance(
      {
        latitude: location?.latitude || 27.504,
        longitude: location?.longitude || 77.669,
      },
      {
        latitude: store?.address?.latitude || store?.address?.city?.latitude || 27.504,
        longitude: store?.address?.longitude || store?.address?.city?.longitude || 77.669,
      }
    );
    setLocalDistance(distance);
  }

  useMotionValueEvent(scrollY, 'change', (latest) => {
    const prev = scrollY.getPrevious();
    if (prev < latest) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }
  });

  useEffect(() => {
    fetchStoreImages();
    fetchLocation();
  }, [store]);

  const distanceLabel =
    localDistance === undefined || localDistance >= 100 * 1000
      ? null
      : localDistance < 1000
        ? `${localDistance.toFixed(0)} m away`
        : localDistance < 10000
          ? `${(localDistance / 1000).toFixed(1)} km away`
          : `${(localDistance / 1000).toFixed(0)} km away`;
  // Nearby: neighbourhood + city. Far away (or distance unknown): city + state, which is what a distant visitor can place.
  const isFar = localDistance === undefined || localDistance >= 50 * 1000;
  const shortLocation = isFar
    ? getAddress(store?.address, '', false)
    : [store?.address?.locality, store?.address?.city?.name].filter((x) => x?.length > 0).join(', ');
  const fullAddress = getAddress(store?.address);
  const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const isPickupOnly = store?.is_pickup === true && !store?.is_delivery;
  const unavailableReason = isPickupOnly
    ? 'Pickup only: ordering is available near this store'
    : store?.category_id === 1
      ? 'Currently not delivering to your location'
      : 'Store is currently closed';
  const todayTiming = (storeTimings || []).find((x) => x?.day_of_week === new Date().getDay() && hasStoreHours(x));
  const openNow = !!todayTiming && isTimingOpenNow(todayTiming);
  const hasTimings = (storeTimings || []).some((x) => hasStoreHours(x));
  const hasCoords = store?.address?.latitude != undefined;
  const mapsHref = `https://www.google.com/maps/search/?api=1&query=${store?.address?.latitude},${store?.address?.longitude}`;
  const reviewCount = store?.stats?.rating_count || 0;
  const dim = isAvailable === false ? 'grayscale' : '';
  const imageClass = `w-full h-52 object-cover rounded-xl ${dim}`;

  const fact = (icon: React.ReactNode, children: React.ReactNode) => (
    <div className="flex items-start gap-2.5 md:gap-3 min-w-0">
      <span className="flex items-center justify-center w-7 h-7 md:w-8 md:h-8 rounded-lg bg-neutral-100 text-neutral-500 flex-shrink-0">
        {icon}
      </span>
      <div className="min-w-0 flex-1 pt-1 text-xs md:text-sm font-semibold text-neutral-700 font-manrope break-words">
        {children}
      </div>
    </div>
  );

  return (
    <section className="bg-white border border-neutral-200/70 shadow-sm rounded-2xl overflow-hidden">
      <div className="grid md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-3 md:gap-6 p-3 md:p-5">
        {/* Gallery: desktop only. Mobile gets a compact thumbnail beside the title. */}
        <div className="hidden md:block min-w-0">
          <SafeImage
            fallback={PLACEHOLDERS.store}
                loader={sanityIoImageLoader}
            src={store?.image || 'https://cdn.jhattse.com/public/consumer/placeholder/store.png'}
            className={imageClass}
            alt={store?.name || 'Store'}
            priority={true}
            width="400"
            height="300"
          />
          {storeImages?.length > 0 && (
            <div className="flex gap-2 mt-2 overflow-x-auto no-scrollbar">
              {storeImages.slice(0, 4).map((image, i) => (
                <SafeImage
                  key={image?.url || i}
                  fallback={PLACEHOLDERS.store}
                loader={sanityIoImageLoader}
                  src={image?.url || 'https://cdn.jhattse.com/public/consumer/placeholder/store.png'}
                  className={`w-20 h-14 object-cover rounded-lg flex-shrink-0 ${dim}`}
                  alt={store?.name || 'Store'}
                  width="160"
                  height="112"
                  loading="lazy"
                />
              ))}
            </div>
          )}
        </div>

        <div className="min-w-0 flex flex-col gap-2.5 md:gap-4 font-manrope">
          <div className="flex flex-col gap-2">
            <div className="flex items-start gap-3">
              <SafeImage
                fallback={PLACEHOLDERS.store}
                loader={sanityIoImageLoader}
                src={store?.image || 'https://cdn.jhattse.com/public/consumer/placeholder/store.png'}
                className={`md:hidden w-16 h-16 object-cover rounded-xl flex-shrink-0 ${dim}`}
                alt={store?.name || 'Store'}
                width="128"
                height="128"
              />
              <div className="min-w-0 flex-1">
                <h1 className="m-0 text-base md:text-2xl font-bold text-neutral-900 leading-snug break-words line-clamp-2 md:line-clamp-none">
                  {store?.name || 'Store'}
                </h1>
                {store?.category?.name && (
                  <span className="text-xs md:text-sm font-semibold text-neutral-500">{store.category.name}</span>
                )}
              </div>
              {store?.stats?.rating_overall > 0 && (
                <div className={`h-7 px-2.5 flex items-center gap-1 rounded-lg flex-shrink-0 ${suggestColor(store.stats.rating_overall)}`}>
                  <Star className="w-4 h-4 text-white" />
                  <span className="text-sm text-white font-bold">{store.stats.rating_overall}</span>
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {store?.verified && (
                <span className="inline-flex items-center gap-1 rounded-full bg-verified text-neutral-50 text-xs font-semibold py-1 pl-1.5 pr-2.5 select-none">
                  <BadgeCheck size={14} /> Trusted Seller
                </span>
              )}
              {store?.is_pickup && (
                <span className="inline-flex items-center gap-1 rounded-full bg-red-50 border border-solid border-red-200 text-red-700 text-xs font-bold uppercase tracking-wide px-2.5 py-1">
                  <Store size={12} /> Pickup
                </span>
              )}
              {store?.is_delivery && (
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-solid border-blue-200 text-blue-700 text-xs font-bold uppercase tracking-wide px-2.5 py-1">
                  <Truck size={12} /> Delivery
                </span>
              )}
              <span className="text-sm font-semibold text-neutral-500">
                {reviewCount > 0 ? (
                  <Link href={`/store/${store?.id}/reviews/${getSafeUrl(store?.name || 'store')}`} prefetch={false} className="text-brand-500 hover:underline">
                    {reviewCount} {reviewCount === 1 ? 'review' : 'reviews'}
                  </Link>
                ) : (
                  'No reviews yet'
                )}
              </span>
            </div>
          </div>

          {isAvailable === false && (
            <div className="flex items-center gap-2 rounded-lg md:rounded-xl bg-amber-50 border border-solid border-amber-200 text-amber-800 text-xs md:text-sm font-semibold px-3 py-1.5 md:py-2">
              <Clock className="w-4 h-4 flex-shrink-0" /> {unavailableReason}
            </div>
          )}

          <div ref={ref} className="grid sm:grid-cols-2 gap-x-6 gap-y-2 md:gap-y-3">
            {hasTimings &&
              fact(
                <Clock className="w-4 h-4" />,
                <button type="button" onClick={() => setInfoOpen(true)} className="p-0 border-0 bg-transparent cursor-pointer text-left inline-flex items-center gap-1.5 flex-wrap text-xs md:text-sm font-semibold font-manrope">
                  <span className={`inline-flex items-center gap-1 ${openNow ? 'text-emerald-600' : 'text-red-500'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${openNow ? 'bg-emerald-500' : 'bg-red-500'}`} />
                    {openNow ? 'Open now' : 'Closed'}
                  </span>
                  <span className="text-neutral-500 font-medium">· {todayTiming ? formatStoreHours(todayTiming) : 'Closed today'}</span>
                  <ChevronRight className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                </button>
              )}
            {(shortLocation || fullAddress) &&
              fact(
                <MapPin className="w-4 h-4" />,
                <button type="button" onClick={() => setInfoOpen(true)} className="p-0 border-0 bg-transparent cursor-pointer text-left inline-flex items-center gap-1 text-neutral-700 font-semibold text-xs md:text-sm font-manrope">
                  <span>{shortLocation || fullAddress}</span>
                  {distanceLabel && <span className="text-neutral-400 font-medium whitespace-nowrap">· {distanceLabel}</span>}
                  <ChevronRight className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                </button>
              )}
            {store?.is_delivery && store?.storemeta?.delivery?.delvery_time?.length > 0 &&
              fact(<Truck className="w-4 h-4" />, <>Delivering in {store.storemeta.delivery.delvery_time}</>)}
          </div>

          {/* Quick actions: icon-only to keep the card light */}
          <div className="flex items-center gap-2 mt-auto">
            {store?.phone && (
              <a href={`tel:${store.phone}`} aria-label="Call store" title="Call" className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-brand-500 text-white hover:opacity-90">
                <Phone className="w-4 h-4" />
              </a>
            )}
            {hasCoords && (
              <Link href={mapsHref} target="_blank" prefetch={false} aria-label="Get directions" title="Directions" className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-neutral-100 text-neutral-700 hover:bg-neutral-200">
                <Navigation className="w-4 h-4" />
              </Link>
            )}
            <RWebShare
              data={{
                title: 'Store',
                text: `${store?.name} is on ${appConfig?.appName}`,
                url: `/store/${store?.id}/${getSafeUrl(store?.name || 'store')}`,
              }}
            >
              <button type="button" aria-label="Share store" title="Share" className="w-9 h-9 inline-flex items-center justify-center rounded-full border-0 bg-neutral-100 text-neutral-700 hover:bg-neutral-200 cursor-pointer">
                <Share2 className="w-4 h-4" />
              </button>
            </RWebShare>
            <div className="ml-auto">
              <StoreReview store_id={store?.id} />
            </div>
          </div>
        </div>
      </div>
      <Modal
        open={infoOpen}
        onCancel={() => setInfoOpen(false)}
        footer={null}
        centered
        title={<span className="block pr-6 font-manrope font-bold text-neutral-900">{store?.name || 'Store'}</span>}
      >
        <div className="flex flex-col gap-4 font-manrope text-sm text-neutral-700">
          {fullAddress && (
            <div className="flex flex-col gap-1">
              <span className="text-xs font-bold uppercase tracking-wide text-neutral-400">Address</span>
              <span className="font-semibold">{fullAddress}</span>
              {hasCoords && (
                <Link href={mapsHref} target="_blank" prefetch={false} className="text-brand-500 font-semibold">
                  Open in Maps{distanceLabel ? ` · ${distanceLabel}` : ''}
                </Link>
              )}
            </div>
          )}
          {store?.phone && (
            <div className="flex flex-col gap-1">
              <span className="text-xs font-bold uppercase tracking-wide text-neutral-400">Phone</span>
              <a href={`tel:${store.phone}`} className="font-semibold text-neutral-700">{store.phone}</a>
            </div>
          )}
          {hasTimings && (
            <div className="flex flex-col gap-1">
              <span className="text-xs font-bold uppercase tracking-wide text-neutral-400">Opening hours</span>
              {weekDays.map((day, i) => {
                const t = storeTimings.find((x) => x.day_of_week === i && hasStoreHours(x));
                return (
                  <div key={day} className={`flex justify-between ${new Date().getDay() === i ? 'font-bold text-neutral-900' : ''}`}>
                    <span>{day}</span>
                    <span>{t ? formatStoreHours(t) : 'Closed'}</span>
                  </div>
                );
              })}
            </div>
          )}
          {store?.is_delivery && store?.storemeta?.delivery?.return_available != null && (
            <div className="flex items-center gap-2">
              <RotateCcw className="w-4 h-4 text-neutral-400" />
              <span className="font-semibold">Returns {store.storemeta.delivery.return_available ? '' : 'not '}available</span>
            </div>
          )}
        </div>
      </Modal>
    </section>
  );
}
